import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  AppShell,
  Navbar,
  Header,
  Text,
  MediaQuery,
  Burger,
  Group,
  UnstyledButton,
  Avatar,
  Box,
  Menu,
  ActionIcon,
  useMantineTheme,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconLogout,
  IconSettings,
  IconChevronDown,
  IconSun,
  IconMoonStars,
  IconMenu2,
} from '@tabler/icons-react';
import { MainLinks } from '../components/MainLinks';
import { useAuth } from '../contexts/AuthContext';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_WIDTH_COLLAPSED = 72;

export function DashboardLayout() {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const [opened, setOpened] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebarCollapsed') === 'true'
  );
  const { user, logout } = useAuth();

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem('sidebarCollapsed', String(next));
      return next;
    });
  };

  const dark = colorScheme === 'dark';
  const paperBg = dark ? '#242424' : '#ffffff';
  const dividerColor = dark ? '#333333' : '#e0e0e0';
  const secondaryText = dark ? '#a0a0a0' : '#616161';
  const hoverBg = theme.fn.rgba(theme.colors.thanos[5], dark ? 0.08 : 0.04);

  const navWidth = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <div className="dashboard-page">
      <AppShell
        styles={{
          main: {
            background: dark ? '#1b1b1b' : '#fafafa',
            position: 'relative',
          },
        }}
        navbarOffsetBreakpoint="sm"
        navbar={
          <Navbar
            p="md"
            hiddenBreakpoint="sm"
            hidden={!opened}
            width={{ base: navWidth }}
            sx={{
              background: paperBg,
              borderRight: `1px solid ${dividerColor}`,
              transition: 'width 0.2s ease',
            }}
          >
            <Navbar.Section grow>
              <MainLinks collapsed={collapsed} />
            </Navbar.Section>
          </Navbar>
        }
        header={
          <Header
            height={60}
            sx={{
              background: paperBg,
              borderBottom: `1px solid ${dividerColor}`,
            }}
          >
            <Group sx={{ height: '100%' }} px={20} position="apart">
              <Group spacing="sm">
                {/* Mobile: open/close drawer */}
                <MediaQuery largerThan="sm" styles={{ display: 'none' }}>
                  <Burger
                    opened={opened}
                    onClick={() => setOpened((o) => !o)}
                    size="sm"
                    color={secondaryText}
                  />
                </MediaQuery>

                {/* Desktop: collapse / expand the sidebar rail.
                    ml nudges the icon onto the sidebar nav-icon column
                    (44px expanded; centered rail when collapsed). */}
                <MediaQuery smallerThan="sm" styles={{ display: 'none' }}>
                  <ActionIcon
                    onClick={toggleCollapsed}
                    variant="subtle"
                    size="lg"
                    ml={collapsed ? 0 : 7}
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    sx={{
                      color: secondaryText,
                      '&:hover': { backgroundColor: hoverBg },
                    }}
                  >
                    <IconMenu2 size={20} />
                  </ActionIcon>
                </MediaQuery>

                <Group spacing={8}>
                  <Text
                    size="xl"
                    weight={700}
                    sx={{
                      color: theme.colors.thanos[5],
                      fontSize: '1.4rem',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    Small Group
                  </Text>
                </Group>
              </Group>

              <Group spacing="lg">
                <Menu
                  width={200}
                  position="bottom-end"
                  transitionProps={{ transition: 'pop-top-right' }}
                  withinPortal
                >
                  <Menu.Target>
                    <UnstyledButton
                      sx={{
                        padding: '4px 10px 4px 4px',
                        borderRadius: 999,
                        transition: 'background 0.15s ease',
                        '&:hover': { backgroundColor: hoverBg },
                      }}
                    >
                      <Group spacing={7}>
                        <Avatar
                          radius="xl"
                          size={30}
                          src={user?.avatarUrl || null}
                          imageProps={{ referrerPolicy: 'no-referrer' }}
                          alt={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                          sx={{
                            backgroundColor: theme.colors.thanos[5],
                            color: '#ffffff',
                            fontWeight: 700,
                          }}
                        >
                          {user?.firstName?.[0]}
                          {user?.lastName?.[0]}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Text size="sm" weight={500}>
                            {user?.firstName} {user?.lastName}
                          </Text>
                        </Box>
                        <IconChevronDown size={12} stroke={1.5} color={secondaryText} />
                      </Group>
                    </UnstyledButton>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      icon={dark ? <IconSun size={14} /> : <IconMoonStars size={14} />}
                      closeMenuOnClick={false}
                      onClick={() => toggleColorScheme()}
                    >
                      {dark ? 'Light mode' : 'Dark mode'}
                    </Menu.Item>
                    <Menu.Item
                      icon={<IconSettings size={14} />}
                      onClick={() => navigate('/settings')}
                    >
                      Settings
                    </Menu.Item>
                    <Menu.Item icon={<IconLogout size={14} />} onClick={logout}>
                      Logout
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
          </Header>
        }
      >
        <Outlet />
      </AppShell>
    </div>
  );
}
